import { memo, useCallback, type FC } from "react";
import { Stack, Typography } from "@mui/material";
import type { CompanyFilters } from "../types";
import type { FilterModule } from "./index";
import { useCompanyFilterSlice, useCompanyFilterActions } from "../hooks";
import { ProductFilter } from "@/components/filters/ProductFilter/ProductFilter";
import { PRODUCT_LIST_UPLOAD_PROPS } from "@/components/filters/ProductFilter/constants";
import type { ProductFilterValue } from "@/components/filters/ProductFilter/types";
import { ConditionSelectFilter } from "@/components/filters/Condition/ConditionSelectFilter";
import { FILTER_MODE } from "@/components/filters/Condition/constants";
import type { FilterMode } from "@/components/filters/Condition/types";
import { InclusionMethod } from "@/generated/company-search";

type BuyerIntentProductsSlice = {
  productIds: number[];
  inclusionMethod: FilterMode;
};
const defaultSlice: BuyerIntentProductsSlice = {
  productIds: [],
  inclusionMethod: FILTER_MODE.ANY,
};

const BuyerIntentProductsFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice("buyerIntentProducts");
  const { setSlice } = useCompanyFilterActions();

  const handleProductsSelect = useCallback(
    (value: ProductFilterValue) => {
      setSlice("buyerIntentProducts", { ...slice, productIds: value.ids });
    },
    [setSlice, slice]
  );

  const handleConditionChange = useCallback(
    (inclusionMethod: FilterMode) => {
      setSlice("buyerIntentProducts", { ...slice, inclusionMethod });
    },
    [setSlice, slice]
  );

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center">
        <Typography variant="body2">Include companies researching</Typography>
        <ConditionSelectFilter
          id="buyer-intent-product-condition"
          value={slice.inclusionMethod}
          onChange={handleConditionChange}
        />
        <Typography variant="body2">of the following products</Typography>
      </Stack>
      <ProductFilter
        selectedProductIds={slice.productIds}
        onProductsSelect={handleProductsSelect}
        uploadProps={PRODUCT_LIST_UPLOAD_PROPS}
        autoCompleteProps={{ disablePortal: false }}
        disableCollectionSelect
      />
    </Stack>
  );
});

BuyerIntentProductsFilter.displayName = "BuyerIntentProductsFilter";

export const buyerIntentProductsModule: FilterModule<BuyerIntentProductsSlice> =
  {
    key: "buyerIntentProducts",
    defaultSlice,
    Component: BuyerIntentProductsFilter,
    toCompanyFilters: (slice) =>
      slice.productIds.length > 0
        ? {
            buyerIntentProducts: [
              {
                ids: slice.productIds,
                inclusionMethod: slice.inclusionMethod as InclusionMethod,
              },
            ],
          }
        : {},
    fromCompanyFilters: (filters: CompanyFilters) => ({
      productIds:
        filters.buyerIntentProducts?.flatMap(
          (p) => p?.ids?.filter((id): id is number => id != null) ?? []
        ) ?? [],
      inclusionMethod:
        (filters.buyerIntentProducts?.[0]?.inclusionMethod as
          | FilterMode
          | undefined) ?? FILTER_MODE.ANY,
    }),
    getChips: (slice, setSlice) =>
      slice.productIds.map((id) => ({
        id: `bi-product-${id}`,
        label: `BI Product: ${id}`,
        filterKey: "buyerIntentProducts" as const,
        onRemove: () =>
          setSlice({
            ...slice,
            productIds: slice.productIds.filter((x) => x !== id),
          }),
      })),
    getCount: (slice) => slice.productIds.length,
  };
